import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Dialog,
  Alert,
  IconButton,
  Checkbox,
  Box,
  Tooltip,
  Select,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DataGrid } from "@mui/x-data-grid";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

//import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
//import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
//import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiBadge from "components/SuiBadge";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
import MontoInput from "common/Monto";
import FocusDatePicker from "common/FocusDatePicker";

import PlanesPagoApi from "api/planesPagoApi";
import useParametrosGenerales from "hooks/useParametrosGenerales";

// Styles
import { useStyles, GridContainer } from "common/Monto/styles";
const createEmptyRow = () => ({
  id: `new-${Date.now()}-${Math.random()}`,
  concepto: "",
  monto: "",
  saved: false,
});

const MovimientosModal = ({ open, onClose, plan_pago, onPlanPagoChange }) => {
  const [editRows, setEditRows] = useState({});
  const classes = useStyles();
  const [error, setError] = useState(undefined);
  const { parametros } = useParametrosGenerales();
  const [ingresos, setIngresos] = useState([createEmptyRow()]);
  const [egresos, setEgresos] = useState([createEmptyRow()]);
  const [totals, setTotals] = useState({
    totalIngresos: 0,
    totalEgresos: 0,
    totalIngresosRecuadados: 0,
    totalEgresosRealizados: 0,
    disponible: 0,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [gridKey, setGridKey] = useState(0);

  const cargarDatos = async () => {
    if (!plan_pago) return;
    try {
      // Cargar plan_pago
      const response = await PlanesPagoApi.fetchMovimientos(plan_pago.id);
      const data = response.data || {};
      const ingrGastArray = Array.isArray(data.movimientos) ? data.movimientos : [];
      if (ingrGastArray.length > 0) {
        const ingresosData = ingrGastArray
          .filter((f) => f.tipo === "INGRESO")
          .map((f) => ({
            id: f.id,
            concepto: f.tipo_ingreso,
            estado: f.estado,
            monto: f.valor,
            fecha_compromiso: f.fecha_compromiso,
            medio_pago: f.medio_pago,
            fecha_realizacion: f.fecha_realizacion,
            saved: true,
          }));
        const egresosData = ingrGastArray
          .filter((f) => f.tipo === "GASTO")
          .map((f) => ({
            id: f.id,
            concepto: f.tipo_gasto,
            estado: f.estado,
            monto: f.valor,
            fecha_compromiso: f.fecha_compromiso,
            medio_pago: f.medio_pago,
            fecha_realizacion: f.fecha_realizacion,
            saved: true,
          }));
        setIngresos(ingresosData.length > 0 ? ingresosData : [createEmptyRow()]);
        setEgresos(egresosData.length > 0 ? egresosData : [createEmptyRow()]);
      } else {
        setIngresos([createEmptyRow()]);
        setEgresos([createEmptyRow()]);
      }
    } catch (error) {
      console.error("Error al cargar los Movimientos:", error);
      setError(obtenerMensajeError(error, "Error al cargar los Movimientos"));
    }
  };

  useEffect(() => {
    if (plan_pago) {
      cargarDatos();
    }
  }, [plan_pago]);

  useEffect(() => {
    const totalIngresos = ingresos.reduce((sum, item) => {
      const monto = parseFloat(item.monto) || 0;
      return sum + monto;
    }, 0);

    const totalEgresos = egresos.reduce((sum, item) => {
      const monto = parseFloat(item.monto) || 0;
      return sum + monto;
    }, 0);

    const totalIngresosRecuadados = ingresos.reduce((sum, item) => {
      let monto = 0;
      if (item.estado) {
        monto = parseFloat(item.monto);
      }
      return sum + monto;
    }, 0);

    const totalEgresosRealizados = egresos.reduce((sum, item) => {
      let monto = 0;
      if (item.estado) {
        monto = parseFloat(item.monto);
      }
      return sum + monto;
    }, 0);

    setTotals({
      totalIngresos,
      totalEgresos,
      totalIngresosRecuadados,
      totalEgresosRealizados,
      disponible: totalIngresosRecuadados - totalEgresosRealizados,
    });
  }, [ingresos, egresos]);

  const handleSaveAll = async () => {
    if (!plan_pago) return;
    if (totals.totalIngresos < plan_pago.monto_total) {
      alert("Los Ingresos Proyectados no cumplen el Monto Total del plan de pagos.");
    }
    setIsSaving(true);

    try {
      // Guardar ingresos
      for (const ingreso of ingresos.filter(
        (i) => (!i.saved || i.changed) && i.concepto && i.monto
      )) {
        const data = {
          plan_pago: plan_pago.id,
          tipo: "INGRESO",
          estado: ingreso.estado || false,
          tipo_ingreso: ingreso.concepto,
          tipo_gasto: null,
          valor: ingreso.monto.toString(),
          fecha_compromiso: ingreso.fecha_compromiso,
          medio_pago: ingreso.medio_pago,
          fecha_realizacion: ingreso.fecha_realizacion,
        };
        let response;
        if (ingreso.id && ingreso.saved) {
          response = await PlanesPagoApi.actualizarIngresoGasto(ingreso.id, data);
        } else {
          response = await PlanesPagoApi.registrarIngresoGasto(data);
        }
        const savedItem = response.data;
        setIngresos((prevIngresos) =>
          prevIngresos.map((i) =>
            i.id === ingreso.id ? { ...i, id: savedItem.id, saved: true, changed: false } : i
          )
        );
      }
      try {
        const updatedPlan = await PlanesPagoApi.obtenerPlanPagoById(plan_pago.id);
        if (updatedPlan?.data) {
          onPlanPagoChange(updatedPlan.data);
        }
      } catch (e) {
        console.error("Error actualizando el Plan de Pago:", e);
      }
      setHasChanges(false);
      cargarDatos();
    } catch (error) {
      console.error("Error al guardar los Ingresos y Gastos:", error);
      setError(obtenerMensajeError(error, "Error al guardar los Ingresos y Gastos"));
    } finally {
      setIsSaving(false);
    }
  };

  const eliminarIngreso = async (id) => {
    const item = ingresos.find((i) => i.id === id);
    if (item?.saved && item.estado) {
      // Si el ingreso tiene estado TRUE (guardado y confirmado), no permitir eliminarlo
      alert("Este ingreso no puede ser eliminado porque ya ha sido confirmado.");
      return;
    }
    if (window.confirm("¿Está seguro(a) de eliminar este registro?")) {
      try {
        const item = ingresos.find((i) => i.id === id);
        if (item?.saved) {
          await PlanesPagoApi.eliminarIngresoGasto(id);
        }
        setIngresos((prevIngresos) => {
          const updatedIngresos = prevIngresos.filter((item) => item.id !== id);
          const newIngresos = updatedIngresos.length === 0 ? [createEmptyRow()] : updatedIngresos;
          setGridKey((prev) => prev + 1);
          return newIngresos;
        });
        setHasChanges(true);
      } catch (error) {
        console.error("Error al eliminar el ingreso del Plan de Pago:", error);
        setError(obtenerMensajeError(error, "Error al eliminar el ingreso del Plan de Pago"));
      }
    }
  };

  const agregarIngreso = () => {
    setIngresos((prev) => [...prev, createEmptyRow()]);
    setGridKey((prev) => prev + 1);
    setHasChanges(true);
  };

  const handleIngresoChange = (id, field, value) => {
    setIngresos((prevIngresos) =>
      prevIngresos.map((item) =>
        item.id === id ? { ...item, [field]: value || "", changed: true } : item
      )
    );
    setHasChanges(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const createColumns = (handleChange, handleDelete, editRows, setEditRows) => [
    {
      field: "actions",
      renderHeader: () => (
        <Tooltip title="Nuevo Ingreso" placement="left">
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%", // Para que se centre verticalmente dentro de la celda
            }}
          >
            <AddCircleIcon
              onClick={agregarIngreso}
              fontSize="large"
              sx={{
                cursor: "pointer",
                color: "black",
                opacity: 1,
              }}
            />
          </Box>
        </Tooltip>
      ),
      width: 100,
      sortable: false,
      align: "center",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <IconButton
          onClick={() => handleDelete(params.row.id)}
          size="small"
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
    {
      field: "estado",
      headerName: "ESTADO",
      renderHeader: () => (
        <SuiTypography variant="caption" fontWeight="medium" color="text">
          ¿?
        </SuiTypography>
      ),
      width: 65,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Checkbox
          name="estado"
          checked={params.row.estado || false} // Asegura un valor booleano
          onChange={(e) => handleChange(params.row.id, "estado", e.target.checked)}
          color="primary"
        />
      ),
    },
    {
      field: "concepto",
      headerName: "CONCEPTO",
      flex: 2,
      minWidth: 130,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <SuiTypography variant="caption" fontWeight="medium" color="text">
          CONCEPTO
        </SuiTypography>
      ),
      renderCell: (params) => {
        const isEditing = editRows[params.row.id]?.concepto;
        return isEditing ? (
          <SuiInput
            value={params.row.concepto || ""}
            onChange={(e) => handleIngresoChange(params.row.id, "concepto", e.target.value)}
            fullWidth
            size="small"
            onBlur={() => {
              const cleaned = (params.row.concepto || "").trim();
              if (cleaned === "") {
                alert("El Concepto no puede estar vacío o solo con espacios.");
              } else {
                setEditRows((prev) => ({
                  ...prev,
                  [params.row.id]: { ...prev[params.row.id], concepto: false },
                }));
              }
            }}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            inputProps={{
              onKeyDown: (e) => {
                e.stopPropagation(); // Solo evita la selección de la fila, no bloquea teclas
              },
            }}
          />
        ) : (
          <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
            <span>{params.row.concepto || <em style={{ color: "#999" }}>Sin dato</em>}</span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditRows((prev) => ({
                  ...prev,
                  [params.row.id]: { ...prev[params.row.id], concepto: true },
                }));
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: "medio_pago",
      headerName: "MEDIO DE PAGO",
      flex: 1,
      minWidth: 160,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <SuiTypography variant="caption" fontWeight="medium" color="text">
          MEDIO DE PAGO
        </SuiTypography>
      ),
      renderCell: (params) => {
        const isEditing = editRows[params.row.id]?.medio_pago;
        return isEditing ? (
          <Select
            name="medio_pago"
            value={params.row.medio_pago || ""}
            fullWidth
            size="small"
            onChange={(e) => {
              handleIngresoChange(params.row.id, "medio_pago", e.target.value);
            }}
            onBlur={() =>
              setEditRows((prev) => ({
                ...prev,
                [params.row.id]: { ...prev[params.row.id], medio_pago: false },
              }))
            }
            autoFocus
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem value="">Seleccione un Medio de Pago</MenuItem>
            <MenuItem value="POR DEFINIR">POR DEFINIR</MenuItem>
            {parametros.MEDIOS_PAGO.filter((medio) => medio !== "POR DEFINIR").map((medio) => (
              <MenuItem key={medio} value={medio}>
                {medio}
              </MenuItem>
            ))}
            {/* Medio de pago guardado que ya no está en la lista configurada */}
            {params.row.medio_pago &&
              params.row.medio_pago !== "POR DEFINIR" &&
              !parametros.MEDIOS_PAGO.includes(params.row.medio_pago) && (
                <MenuItem value={params.row.medio_pago}>{params.row.medio_pago}</MenuItem>
              )}
          </Select>
        ) : (
          <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
            <span>{params.row.medio_pago || <em style={{ color: "#999" }}>Sin dato</em>}</span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditRows((prev) => ({
                  ...prev,
                  [params.row.id]: { ...prev[params.row.id], medio_pago: true },
                }));
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: "monto",
      headerName: "MONTO",
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <SuiTypography variant="caption" fontWeight="medium" color="text">
          MONTO
        </SuiTypography>
      ),
      renderCell: (params) => {
        const isEditing = editRows[params.row.id]?.monto;
        return isEditing ? (
          <MontoInput
            value={params.row.monto}
            onChange={(value) => handleChange(params.row.id, "monto", value)}
            onBlurExterno={() =>
              setEditRows((prev) => ({
                ...prev,
                [params.row.id]: { ...prev[params.row.id], monto: false },
              }))
            }
            autoFocus
          />
        ) : (
          <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
            <span>
              {params.row.monto ? (
                new Intl.NumberFormat("es-CO", {
                  style: "currency",
                  currency: "COP",
                  minimumFractionDigits: 0,
                }).format(params.row.monto)
              ) : (
                <em style={{ color: "#999" }}>Sin monto</em>
              )}
            </span>
            <IconButton
              size="small"
              onClick={() => {
                //e.stopPropagation();
                setEditRows((prev) => ({
                  ...prev,
                  [params.row.id]: { ...prev[params.row.id], monto: true },
                }));
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: "fecha_compromiso",
      headerName: "FECHA",
      renderHeader: () => (
        <SuiTypography variant="caption" fontWeight="medium" color="text">
          FECHA COMPROMISO
        </SuiTypography>
      ),
      flex: 2,
      minWidth: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isEditing = editRows[params.row.id]?.fecha_compromiso;
        return isEditing ? (
          <FocusDatePicker
            value={params.row.fecha_compromiso}
            format="DD/MM/YYYY"
            onChange={(value) => handleChange(params.row.id, "fecha_compromiso", value)}
            onClickAway={() =>
              setEditRows((prev) => ({
                ...prev,
                [params.row.id]: { ...prev[params.row.id], fecha_compromiso: false },
              }))
            }
          />
        ) : (
          <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
            <span>
              {params.row.fecha_compromiso ? (
                dayjs(params.row.fecha_compromiso).format("DD/MM/YYYY")
              ) : (
                <em style={{ color: "#999" }}>Sin fecha</em>
              )}
            </span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditRows((prev) => ({
                  ...prev,
                  [params.row.id]: { ...prev[params.row.id], fecha_compromiso: true },
                }));
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: "fecha_realizacion",
      headerName: "FECHA_REALIZACION",
      renderHeader: () => (
        <SuiTypography variant="caption" fontWeight="medium" color="text">
          FECHA REALIZACIÓN
        </SuiTypography>
      ),
      flex: 2,
      minWidth: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isEditing = editRows[params.row.id]?.fecha_realizacion;
        return isEditing ? (
          <FocusDatePicker
            value={params.row.fecha_realizacion}
            format="DD/MM/YYYY"
            onChange={(value) => handleChange(params.row.id, "fecha_realizacion", value)}
            onClickAway={() =>
              setEditRows((prev) => ({
                ...prev,
                [params.row.id]: { ...prev[params.row.id], fecha_realizacion: false },
              }))
            }
          />
        ) : (
          <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
            <span>
              {params.row.fecha_realizacion ? (
                dayjs(params.row.fecha_realizacion).format("DD/MM/YYYY")
              ) : (
                <em style={{ color: "#999" }}>Sin fecha</em>
              )}
            </span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditRows((prev) => ({
                  ...prev,
                  [params.row.id]: { ...prev[params.row.id], fecha_realizacion: true },
                }));
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const hCClose = () => {
    setError(undefined);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={hCClose}
      maxWidth={false} // ❗ Permite ancho completo personalizado
      fullWidth
      PaperProps={{
        sx: {
          width: "95vw", // Ancho deseado (puedes ajustar)
          height: "90vh", // Altura más grande que 600px
          maxWidth: "none", // Evita límite por defecto
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Card sx={{ p: 1, m: 2, boxShadow: "none", overflow: "hidden" }}>
        <SuiBox
          sx={{
            maxHeight: "70vh", // Limitar altura del contenido
            overflowY: "auto", // Scroll en caso de que sea necesario
          }}
        >
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 2 }}>
            <Grid size={12}>
              <SuiBox display="center" justifyContent="center" alignItems="center" gap={1}>
                <SuiTypography variant="h3" fontWeight="dark">
                  Balance Financiero
                </SuiTypography>
              </SuiBox>
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 2 }}>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="dark">
                <span style={{ fontSize: "24px" }}>
                  Proceso: {plan_pago ? plan_pago.proceso_identificador : "No tiene proceso"}
                </span>
              </SuiTypography>
            </Grid>
            <Grid size={4}>
              <SuiBox display="flex" justifyContent="center" alignItems="center" gap={1}>
                <SuiButton
                  variant="gradient"
                  buttonColor="dark"
                  onClick={handleSaveAll}
                  disabled={!hasChanges || isSaving}
                  startIcon={<SaveIcon />}
                >
                  {isSaving ? "Guardando..." : "Guardar"}
                </SuiButton>
              </SuiBox>
            </Grid>
            <Grid size={4}>
              <Box display="flex" justifyContent="right" alignItems="right">
                <Tooltip title="Saldo Pendiente" placement="left">
                  <SuiBox display="flex" alignItems="center" gap={1}>
                    <WarningAmberIcon fontSize="large" />
                    <SuiBadge
                      className={classes.badge}
                      variant="gradient"
                      color={plan_pago?.saldo_pendiente > 0 ? "error" : "success"}
                      size="large"
                      badgeContent={
                        <span style={{ fontSize: "24px", fontWeight: "bold" }}>
                          {formatCurrency(plan_pago?.saldo_pendiente)}
                        </span>
                      }
                      container
                    />
                  </SuiBox>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 2 }}>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                <span style={{ fontSize: "24px" }}>
                  Cliente: {plan_pago ? plan_pago.cliente_nombre : "No tiene Cliente"}
                </span>
              </SuiTypography>
            </Grid>
            <Grid size={4}>
              <Box display="flex" justifyContent="center" alignItems="center">
                <Box display="flex" alignItems="center">
                  {totals.totalIngresos < plan_pago?.monto_total ? (
                    // Caso 1: Menor que monto_total (ERROR)
                    <>
                      <WarningAmberIcon fontSize="large" sx={{ color: "error.main", mr: 1.5 }} />
                      <SuiTypography
                        variant="button"
                        fontWeight="light"
                        sx={{ color: "error.main", mr: 1.5 }}
                      >
                        Los Ingresos no alcanzan el Monto del plan.
                      </SuiTypography>
                    </>
                  ) : Number(totals.totalIngresos) === Number(plan_pago?.monto_total) ? (
                    // Caso 2: Igual a monto_total (ÉXITO)
                    <>
                      <CheckCircleIcon fontSize="large" sx={{ color: "success.main", mr: 1.5 }} />
                      <SuiTypography
                        variant="button"
                        fontWeight="light"
                        sx={{ color: "success.main", mr: 1.5 }}
                      >
                        Ingresos iguales al Monto del plan.
                      </SuiTypography>
                    </>
                  ) : (
                    // Caso 3: Mayor que monto_total (ADVERTENCIA)
                    <>
                      <WarningAmberIcon fontSize="large" sx={{ color: "warning.main", mr: 1.5 }} />
                      <SuiTypography
                        variant="button"
                        fontWeight="light"
                        sx={{ color: "warning.main", mr: 1.5 }}
                      >
                        Los Ingresos superan el Monto del plan.
                      </SuiTypography>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid size={4}>
              <Box display="flex" justifyContent="right" alignItems="right">
                <Tooltip title="Monto Total" placement="left">
                  <Box display="flex" alignItems="center">
                    <SuiBadge
                      className={classes.badge}
                      variant="gradient"
                      color={totals.totalIngresos < plan_pago?.monto_total ? "error" : "success"}
                      size="large"
                      badgeContent={
                        <span style={{ fontSize: "24px", fontWeight: "bold" }}>
                          {formatCurrency(plan_pago?.monto_total)}
                        </span>
                      }
                      container
                    />
                  </Box>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 1 }}>
            <Grid size={4}>
              <SuiBox display="flex" justifyContent="left" alignItems="left" gap={1}>
                <SuiBadge
                  className={classes.badge}
                  variant="gradient"
                  color="info"
                  size="large"
                  badgeContent={plan_pago?.estado_plan}
                  container
                />
              </SuiBox>
            </Grid>
            <Grid size={4}>
              <SuiBox display="flex" justifyContent="center" alignItems="center" gap={1}>
                <Tooltip title="Total Ingresos Recaudados" placement="left">
                  <SuiBox display="flex" alignItems="center" gap={1}>
                    <CreditScoreIcon fontSize="large" />
                    <SuiBadge
                      variant="gradient"
                      color="success"
                      size="large"
                      badgeContent={
                        <span style={{ fontSize: "24px", fontWeight: "bold" }}>
                          {formatCurrency(totals.totalIngresosRecuadados)}
                        </span>
                      }
                      container
                    />
                  </SuiBox>
                </Tooltip>
              </SuiBox>
            </Grid>
            <Grid size={4}>
              <SuiBox display="flex" justifyContent="right" alignItems="right" gap={1}>
                <Tooltip title="Total Ingresos Proyectados" placement="left">
                  <SuiBox display="flex" alignItems="center" gap={1}>
                    <CurrencyExchangeIcon fontSize="large" />
                    <SuiBadge
                      className={classes.badge}
                      variant="gradient"
                      color={totals.totalIngresos < plan_pago?.monto_total ? "error" : "success"}
                      size="large"
                      badgeContent={
                        <span style={{ fontSize: "24px", fontWeight: "bold" }}>
                          {formatCurrency(totals.totalIngresos)}
                        </span>
                      }
                      container
                    />
                  </SuiBox>
                </Tooltip>
              </SuiBox>
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 1 }}>
            <Grid size={12}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 1 }}>
            <Grid size={12}>
              <SuiBox>
                <GridContainer customHeaderHeight={45}>
                  <DataGrid
                    key={`ingresos-${gridKey}`}
                    rows={ingresos}
                    columns={createColumns(
                      handleIngresoChange,
                      eliminarIngreso,
                      editRows,
                      setEditRows
                    )}
                    hideFooter
                    hideFooterPagination
                    hideFooterSelectedRowCount
                    disableColumnMenu
                    disableColumnFilter
                    disableColumnSelector
                    disableSelectionOnClick
                    //headerHeight={50}
                    rowHeight={50}
                    getRowHeight={() => 50}
                    columnBuffer={15}
                    sx={{ height: "450px" }}
                    getRowId={(row) => row.id}
                  />
                </GridContainer>
              </SuiBox>
            </Grid>
          </Grid>
        </SuiBox>
      </Card>
    </Dialog>
  );
};

MovimientosModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  plan_pago: PropTypes.object,
  onPlanPagoChange: PropTypes.func.isRequired,
};

export default MovimientosModal;
